import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Flex, Input, Modal, Select, Typography, Upload, message } from '@/shared/antd-imports';
import { InboxOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import { useUploadProjectDocumentMutation } from '@/store/api';
import {
  CLIENT_UPLOAD_CATEGORIES,
  CLIENT_UPLOAD_EXTENSIONS,
  CLIENT_UPLOAD_MAX_BYTES,
  type ClientUploadCategory,
} from '@/types/project-documents';
import { documentCategoryLabel } from './document-utils';

interface DocumentUploadModalProps {
  projectId: string;
  onClose: () => void;
}

const MAX_TITLE = 200;
const MAX_DESCRIPTION = 2000;
const ACCEPT = CLIENT_UPLOAD_EXTENSIONS.map(extension => `.${extension}`).join(',');

/**
 * DOC-5 second half (stream mlo): the client sends a file to the provider. It lands in the project's documents as
 * "Received from client" and the project manager is notified. Same limits as the server: one file up to 10 MB, the
 * allowed types only (checked here for a quick answer, and again on the server).
 */
export const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({ projectId, onClose }) => {
  const { t } = useTranslation();
  const [upload, { isLoading }] = useUploadProjectDocumentMutation();
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<ClientUploadCategory>('client_content');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const pick = (candidate: File): boolean => {
    setSubmitError(null);
    const extension = extensionOf(candidate.name);
    if (!(CLIENT_UPLOAD_EXTENSIONS as readonly string[]).includes(extension)) {
      setFile(null);
      setFileError(t('documents.upload.typeError', { defaultValue: 'This file type cannot be uploaded. Use a document, sheet, slide, image or ZIP file.' }));
    } else if (candidate.size > CLIENT_UPLOAD_MAX_BYTES) {
      setFile(null);
      setFileError(t('documents.upload.sizeError', { defaultValue: 'The file is larger than 10 MB.' }));
    } else if (candidate.size === 0) {
      setFile(null);
      setFileError(t('documents.upload.emptyError', { defaultValue: 'The file is empty.' }));
    } else {
      setFile(candidate);
      setFileError(null);
    }
    return false; // never upload on drop: the file is sent with the form
  };

  const submit = async () => {
    if (!file) {
      setFileError(t('documents.upload.fileRequired', { defaultValue: 'Choose a file to upload.' }));
      return;
    }
    setSubmitError(null);
    try {
      const fileData = await readAsDataUrl(file);
      const result = await upload({
        projectId,
        fileName: file.name,
        fileData,
        title: title.trim() || undefined,
        description: description.trim() || undefined,
        category,
      }).unwrap();
      if (result && result.done === false) throw new Error(result.message);
      message.success(t('documents.upload.success', { defaultValue: 'Thank you. Your file was sent to your provider.' }));
      onClose();
    } catch (error) {
      setSubmitError(uploadErrorMessage(t, errorCodeOf(error)));
    }
  };

  const fileList: UploadFile[] = file ? [{ uid: 'selected', name: file.name, status: 'done', size: file.size }] : [];

  return (
    <Modal
      open
      title={t('documents.upload.title', { defaultValue: 'Send a file to your provider' })}
      onCancel={onClose}
      destroyOnClose
      footer={
        <Flex gap={8} justify="flex-end" wrap="wrap">
          <Button onClick={onClose} disabled={isLoading}>
            {t('documents.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button type="primary" loading={isLoading} onClick={() => void submit()}>
            {t('documents.upload.submit', { defaultValue: 'Upload' })}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={12}>
        <Typography.Text type="secondary">
          {t('documents.upload.intro', {
            defaultValue: 'The file is added to the project as "Received from client" and your provider is told. One file at a time, up to 10 MB.',
          })}
        </Typography.Text>
        <div>
          <Upload.Dragger
            accept={ACCEPT}
            multiple={false}
            maxCount={1}
            beforeUpload={pick}
            fileList={fileList}
            onRemove={() => {
              setFile(null);
              return true;
            }}
            aria-describedby={fileError ? 'document-upload-file-error' : undefined}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined aria-hidden />
            </p>
            <p className="ant-upload-text">
              {t('documents.upload.drop', { defaultValue: 'Click or drag a file here' })}
            </p>
            <p className="ant-upload-hint">
              {t('documents.upload.hint', { defaultValue: 'PDF, Word, Excel, PowerPoint, text, CSV, images or ZIP · up to 10 MB' })}
            </p>
          </Upload.Dragger>
          {fileError && (
            <Typography.Text type="danger" id="document-upload-file-error" role="alert" style={{ fontSize: 13 }}>
              {fileError}
            </Typography.Text>
          )}
        </div>
        <div>
          <Typography.Text strong>
            <label htmlFor="document-upload-title">{t('documents.upload.titleLabel', { defaultValue: 'Title (optional)' })}</label>
          </Typography.Text>
          <Input
            id="document-upload-title"
            value={title}
            onChange={event => setTitle(event.target.value)}
            maxLength={MAX_TITLE}
            placeholder={t('documents.upload.titlePlaceholder', { defaultValue: 'The file name is used if empty' })}
            style={{ marginTop: 4 }}
          />
        </div>
        <div>
          <Typography.Text strong>
            <label htmlFor="document-upload-category">{t('documents.category', { defaultValue: 'Type' })}</label>
          </Typography.Text>
          <Select<ClientUploadCategory>
            id="document-upload-category"
            value={category}
            onChange={setCategory}
            options={CLIENT_UPLOAD_CATEGORIES.map(value => ({ value, label: documentCategoryLabel(t, value) }))}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>
        <div>
          <Typography.Text strong>
            <label htmlFor="document-upload-description">{t('documents.upload.descriptionLabel', { defaultValue: 'Note for your provider (optional)' })}</label>
          </Typography.Text>
          <Input.TextArea
            id="document-upload-description"
            value={description}
            onChange={event => setDescription(event.target.value)}
            maxLength={MAX_DESCRIPTION}
            showCount
            autoSize={{ minRows: 2, maxRows: 6 }}
            style={{ marginTop: 4 }}
          />
        </div>
        {submitError && <Alert type="error" showIcon message={submitError} />}
      </Flex>
    </Modal>
  );
};

// Helper functions

const extensionOf = (name: string): string => {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
};

const readAsDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });

const errorCodeOf = (error: unknown): string | null => {
  const data = (error as { data?: { body?: { code?: unknown } } } | null)?.data;
  const code = data?.body?.code;
  return typeof code === 'string' ? code : null;
};

type Translate = ReturnType<typeof useTranslation>['t'];

const uploadErrorMessage = (t: Translate, code: string | null): string => {
  switch (code) {
    case 'invalid_file_type':
      return t('documents.upload.typeError', { defaultValue: 'This file type cannot be uploaded. Use a document, sheet, slide, image or ZIP file.' });
    case 'file_too_large':
      return t('documents.upload.sizeError', { defaultValue: 'The file is larger than 10 MB.' });
    case 'too_many_uploads':
      return t('documents.upload.limitError', { defaultValue: 'You reached today\'s upload limit for this project. Please try again tomorrow.' });
    case 'sign_in_required':
      return t('documents.upload.signIn', { defaultValue: 'Sign in with your own account to upload files.' });
    default:
      return t('documents.upload.error', { defaultValue: 'The file could not be uploaded. Please try again.' });
  }
};

export default DocumentUploadModal;
